<template>
  <div class="request-contact-info-main-section mt-4">
    <section-title
      content="Información de Servicio Educativo"
      :has-line="true"
    />
    <div class="px-4 pb-4">
      <div class="row">
        <div class="col-md">
          <validation-provider
            v-slot="{ errors }"
            name="locationTypeEducationalService"
            rules="required"
          >
            <label class="data-title mb-n2">
              Lugar:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.locationType.name | empty }}
            </span>
            <custom-drop-down
              v-else
              v-model="model.locationType"
              :clearable="false"
              custom-error-msg="El lugar es requerido."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="locationTypeOptions"
              placeholder="Seleccione"
              @input="onChangeLocationType"
            />
          </validation-provider>
        </div>
        <div class="col-md">
          <validation-provider
            v-slot="{ errors }"
            name="regionEducationalService"
            rules="required"
          >
            <label class="data-title mb-n2">
              Región:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.region.name | empty }}
            </span>
            <custom-drop-down
              v-else
              v-model="model.region"
              :clearable="false"
              custom-error-msg="La región es requerida."
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="regions"
              placeholder="Seleccione"
              @input="onChangeRegion"
            />
          </validation-provider>
        </div>
        <div class="col-md">
          <validation-provider
            v-slot="{ errors }"
            name="districtEducationalService"
            rules="required"
          >
            <label class="data-title mb-n2">
              Distrito:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.district.name | empty }}
            </span>
            <custom-drop-down
              v-else
              :clearable="false"
              custom-error-msg="El distrito es requerido."
              :disabled="disableDristric"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="districts"
              placeholder="Seleccione"
              :value="model.district"
              @input="onChangeDistrict"
            />
          </validation-provider>
        </div>
      </div>
      <div class="row mt-4">
        <div class="col-md-4">
          <validation-provider
            v-slot="{ errors }"
            name="municipalityEducationalService"
            rules="required"
          >
            <label class="data-title mb-n2">
              Munipicio:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.municipality.name | empty }}
            </span>
            <custom-drop-down
              v-else
              :clearable="false"
              custom-error-msg="El municipio es requerido."
              :disabled="disableMunicipality"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="municipalities"
              placeholder="Seleccione"
              :value="model.municipality"
              @input="onChangeMunicipality"
            />
          </validation-provider>
        </div>
        <div class="col-md">
          <validation-provider
            v-slot="{ errors }"
            name="schoolEducationalService"
            rules="required"
          >
            <label class="data-title mb-n2">
              Escuela:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.school.name | empty }}
            </span>
            <custom-drop-down
              v-else
              :clearable="false"
              custom-error-msg="La escuela es requerida."
              :disabled="disableSchool"
              :error="errors[0]"
              fieldkey="id"
              fieldname="name"
              :options="schools"
              placeholder="Seleccione"
              :value="model.school"
              @input="onChangeSchool"
            />
          </validation-provider>
        </div>
        <div v-show="showName" class="col-md">
          <validation-provider
            v-slot="{ errors }"
            name="nameEducationalService"
            :rules="showName ? 'required' : ''"
          >
            <label class="data-title mb-n2">
              Nombre:
            </label>
            <span v-if="isReadOnly" class="data-content d-block">
              {{ model.privateLocationName | empty }}
            </span>
            <custom-input
              v-else
              v-model="model.privateLocationName"
              :custom-error-msg="'El nombre es requerido.'"
              :error="errors[0]"
              @input="onValueChange"
            />
          </validation-provider>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import LocationTypes from '@/utils/constants/locationTypes';

import {
  getMunicipalities,
  getRegions,
  getDistricts,
  getSchools,
} from '@/services/common/location.api';

import { ValidationProvider } from 'vee-validate';

export default {
  name: 'RequestEducationalServiceInfo',
  components: {
    CustomInput,
    CustomDropDown,
    SectionTitle,
    ValidationProvider,
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    defaultValue: {
      type: Object,
      default: () => {},
    },
    locationTypes: {
      type: Array,
      required: true,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      municipalities: [],
      regions: [],
      districts: [],
      schools: [],
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    defaultEmptyValue() {
      return { name: '' };
    },
    locationTypeOptions() {
      return [...this.locationTypes];
    },
    disableDristric() {
      if (!this.model.region?.id) return true;
      if (!this.model.district?.id) return false;

      return !this.districts.some((x) => x.id === this.model.district.id);
    },
    disableMunicipality() {
      if (!this.model.district?.id) return true;
      if (!this.model.municipality?.id) return false;
      return !this.municipalities.some(
        (x) => x.id === this.model.municipality.id
      );
    },
    disableSchool() {
      if (!this.model.municipality?.id) return true;
      if (!this.model.school?.id) return false;
      return !this.schools.some((x) => x.id === this.model.school.id);
    },
    showName() {
      if (!this.model?.locationType?.id) return true;

      return [
        LocationTypes.CareCenter,
        LocationTypes.PrivateSchools,
        LocationTypes.HeadStart,
      ].includes(this.model.locationType.id);
    },
    getQueryParams() {
      return {
        academicYearId: this.currentAcademicYearId,
        schoolTypes: this.model.locationType?.id ?? null,
        regionId: this.model.region?.id ?? null,
        districtId: this.model.district?.id ?? null,
        municipalityId: this.model.municipality?.id ?? null,
      };
    },
  },
  methods: {
    async onChangeLocationType(item) {
      this.model.region = null;
      this.onChangeDistrict();
      if (item) {
        await this.requestDistricts(this.getQueryParams);
      }
    },
    async onChangeRegion(item) {
      this.onChangeDistrict();
      if (item) await this.requestDistricts(this.getQueryParams);
    },
    async onChangeDistrict(item) {
      this.model.district = item ?? this.defaultEmptyValue;
      this.onChangeMunicipality();
      if (item) await this.requestMunicipalities(this.getQueryParams);
    },
    async onChangeMunicipality(item) {
      this.model.municipality = item ?? this.defaultEmptyValue;
      this.onChangeSchool();
      if (item) await this.requestSchools(this.getQueryParams);
    },
    onChangeSchool(item) {
      this.model.school = item ?? this.defaultEmptyValue;
      this.onValueChange();
    },
    async requestMunicipalities(params = {}) {
      const { data } = await getMunicipalities(params);
      this.municipalities = [...new Map(data.map((x) => [x.id, x])).values()];
    },
    async requestRegions(params = {}) {
      const { data } = await getRegions(params);
      this.regions = [...new Map(data.map((x) => [x.id, x])).values()];
    },
    async requestDistricts(params = {}) {
      const { data } = await getDistricts(params);
      this.districts = [...new Map(data.map((x) => [x.id, x])).values()];
    },
    async requestSchools(params = {}) {
      const { data } = await getSchools(params);
      this.schools = [...new Map(data.map((x) => [x.id, x])).values()];
    },

    async setDefaultLocationValue(value) {
      if (!value) return;

      this.model.locationType =
        this.locationTypes.find((y) => y.id === this.model.locationType?.id) ??
        value.locationType;

      await this.requestRegions({ academicYearId: this.currentAcademicYearId });

      this.model.region =
        this.regions.find((x) => x.id === value.region?.id) ?? value.region;

      await this.requestDistricts(this.getQueryParams);

      this.model.district =
        this.districts.find((x) => x.id === value.district?.id) ??
        value.district;

      await this.requestMunicipalities(this.getQueryParams);

      this.model.municipality =
        this.municipalities.find((x) => x.id === value.municipality?.id) ??
        value.municipality;

      await this.requestSchools(this.getQueryParams);

      this.model.school =
        this.schools.find((x) => x.id === value.school?.id) ?? value.school;
    },
    onValueChange() {
      if (!this.showName) this.model.name = null;
      this.$emit('input', this.model);
    },
  },
  watch: {
    defaultValue: async function(value, oldValue) {
      if (value !== oldValue) {
        this.setDefaultLocationValue(value);
      }
    },
  },
};
</script>
